import { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DollarSign, Package, Receipt, TrendingDown } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const CATEGORIES = [
  { key: 'salary', label: 'Salary', icon: Receipt },
  { key: 'alat', label: 'Alat', icon: Package },
  { key: 'pajak', label: 'Pajak', icon: DollarSign },
];

const CATEGORY_COLORS = {
  salary: { bg: 'bg-blue-500/10', border: 'border-blue-500/25', text: 'text-blue-400' },
  alat: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', text: 'text-emerald-400' },
  pajak: { bg: 'bg-amber-500/10', border: 'border-amber-500/25', text: 'text-amber-400' },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MONTH_NAMES_ID = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

const formatMonthShort = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_NAMES_ID[m - 1]} ${ym.slice(2, 4)}`;
};

const initialExpenseForm = {
  category: 'salary',
  amount: '',
  title: '',
  expense_date: new Date().toISOString().slice(0, 10),
  notes: '',
};

export default function Accounting() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
  const [activeTab, setActiveTab] = useState('accrue');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialExpenseForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchAccrue = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/accounting/accrue`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Gagal mengambil data accrue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'accrue') fetchAccrue();
  }, [activeTab]);

  const openAddModal = (category) => {
    setFormData({ ...initialExpenseForm, category: category || 'salary' });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.amount || Number(formData.amount) <= 0) {
      alert('Judul dan jumlah harus diisi.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        category: formData.category,
        amount: Number(formData.amount),
        title: formData.title.trim(),
        expense_date: formData.expense_date,
        notes: formData.notes.trim(),
      };
      await axios.post(`${API}/accounting/accrue/expenses`, payload, { withCredentials: true });
      setIsModalOpen(false);
      setFormData(initialExpenseForm);
      fetchAccrue();
    } catch (err) {
      alert(err.response?.data?.detail || 'Gagal menyimpan expense.');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: 'accrue', label: 'Accrue' },
    { key: 'depreciation', label: 'Depreciation' },
  ];

  return (
    <DashboardLayout>
      <Header title="Accounting" />
      <div className="space-y-6 p-8" data-testid="accounting-page">
        <div className="flex gap-2 border-b border-white/10 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-sm px-4 py-2 text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30'
                  : 'text-zinc-400 hover:text-zinc-200 border border-transparent hover:border-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'accrue' && (
          <AccrueContent
            data={data}
            loading={loading}
            onAddExpense={openAddModal}
          />
        )}
        {activeTab === 'depreciation' && <DepreciationContent />}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md border-white/10 bg-zinc-950 text-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Tambah Expense
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Category</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="mt-1 w-full rounded-sm border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Judul</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Judul expense"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Jumlah (Rp)</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Tanggal (Backdate)</Label>
              <Input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                className="mt-1 border-white/10 bg-zinc-900 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Catatan</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Opsional"
                className="mt-1 border-white/10 bg-zinc-900 text-white"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/10 bg-black/30 text-white hover:bg-zinc-800"
              >
                Batal
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-black hover:bg-[#C59A27]">
                {submitting ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function AccrueContent({ data, loading, onAddExpense }) {
  const cardStyle = {
    salary: {
      gradient: 'from-blue-500/[0.08] to-blue-600/[0.03]',
      border: 'border-blue-500/20',
      accent: 'text-blue-400',
      dot: 'bg-blue-400',
    },
    alat: {
      gradient: 'from-emerald-500/[0.08] to-emerald-600/[0.03]',
      border: 'border-emerald-500/20',
      accent: 'text-emerald-400',
      dot: 'bg-emerald-400',
    },
    pajak: {
      gradient: 'from-amber-500/[0.08] to-amber-600/[0.03]',
      border: 'border-amber-500/20',
      accent: 'text-amber-400',
      dot: 'bg-amber-400',
    },
  };

  const sortedExpenses = useMemo(() => {
    return [...(data?.expenses || [])].sort(
      (a, b) => String(b.expense_date || '').localeCompare(String(a.expense_date || ''))
    );
  }, [data?.expenses]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
      </div>
    );
  }

  if (!data) return null;

  const { balances, months, category_monthly_accrual, category_monthly_expenses } = data;

  const buildTableData = () => {
    return CATEGORIES.map((cat) => {
      const bal = balances?.[cat.key] || {};
      const initial = bal.initial_balance || 0;
      const monthlyAccrual = category_monthly_accrual?.[cat.key] || {};
      const monthlyExpenses = category_monthly_expenses?.[cat.key] || {};

      let runningBalance = initial;
      const monthData = months.map((m) => {
        const acc = monthlyAccrual[m] || 0;
        const used = monthlyExpenses[m] || 0;
        runningBalance = runningBalance + acc - used;
        return { month: m, acc, used, cutOff: runningBalance };
      });

      return { category: cat, initial, months: monthData };
    });
  };

  const tableData = buildTableData();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {CATEGORIES.map((cat) => {
          const bal = balances?.[cat.key] || {};
          const s = cardStyle[cat.key];
          const Icon = cat.icon;
          return (
            <div
              key={cat.key}
              className={`relative overflow-hidden rounded-lg border ${s.border} bg-gradient-to-br ${s.gradient} p-5`}
            >
              <div className="absolute top-0 right-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-white/[0.03]" />
              <div className="flex items-center gap-2 mb-4">
                <div className={`h-2 w-2 rounded-full ${s.dot}`} />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
                  {cat.label}
                </span>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${s.accent}`}>
                {formatCurrency(bal.current_balance)}
              </p>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500">
                <span>Saldo Awal {formatCurrency(bal.initial_balance)}</span>
              </div>
              <div className="mt-1 flex items-center gap-4 text-xs">
                <span className="text-emerald-400/80">+{formatCurrency(bal.monthly_accrual)}/bln</span>
                {bal.total_expenses > 0 && (
                  <span className="text-red-400/80">-{formatCurrency(bal.total_expenses)}</span>
                )}
              </div>
              <button
                onClick={() => onAddExpense(cat.key)}
                className="mt-3 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-zinc-400 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
              >
                + Tambah Pengeluaran
              </button>
            </div>
          );
        })}
      </div>

      {/* Monthly Table */}
      <div className="rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="border-b border-white/[0.06] px-5 py-3.5">
          <h3 className="text-sm font-semibold text-white/90">Saldo Accrue Bulanan</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th rowSpan={2} className="sticky left-0 z-20 bg-zinc-950 px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  Item
                </th>
                <th rowSpan={2} className="sticky left-[88px] z-20 bg-zinc-950 px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  Saldo Awal
                </th>
                {months.map((m) => (
                  <th
                    key={m + '-grp'}
                    colSpan={3}
                    className="border-l border-white/[0.08] px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#D4AF37]"
                  >
                    {formatMonthShort(m)}
                  </th>
                ))}
              </tr>
              <tr className="border-b border-white/[0.06]">
                {months.map((m) => (
                  <Fragment key={m + '-sub'}>
                    <th className="border-l border-white/[0.04] px-2 py-2 text-right text-[9px] font-semibold uppercase tracking-[0.06em] text-zinc-500">
                      Tambahan
                    </th>
                    <th className="px-2 py-2 text-right text-[9px] font-semibold uppercase tracking-[0.06em] text-zinc-500">
                      Penggunaan
                    </th>
                    <th className="bg-emerald-500/[0.08] px-2 py-2 text-right text-[9px] font-semibold uppercase tracking-[0.06em] text-emerald-400">
                      Saldo
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr
                  key={row.category.key}
                  className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${idx % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                >
                  <td className="sticky left-0 z-10 bg-zinc-950 px-4 py-3 font-medium text-zinc-300">
                    {row.category.label}
                  </td>
                  <td className="sticky left-[88px] z-10 bg-zinc-950 px-4 py-3 text-right font-semibold text-white">
                    {formatCurrency(row.initial)}
                  </td>
                  {row.months.map((md) => (
                    <Fragment key={md.month + '-cells'}>
                      <td className="border-l border-white/[0.04] px-2 py-3 text-right text-emerald-400/90">
                        {formatCurrency(md.acc)}
                      </td>
                      <td className={`px-2 py-3 text-right ${md.used > 0 ? 'text-red-400/90' : 'text-zinc-600'}`}>
                        {formatCurrency(md.used)}
                      </td>
                      <td className="bg-emerald-500/[0.06] px-2 py-3 text-right font-semibold text-emerald-300">
                        {formatCurrency(md.cutOff)}
                      </td>
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expense List */}
      <div className="rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-3.5">
          <h3 className="text-sm font-semibold text-white/90">Pengeluaran Accrue</h3>
          <Button onClick={() => onAddExpense('salary')} size="sm" className="bg-[#D4AF37] text-black hover:bg-[#C59A27] rounded-md h-8 text-xs font-medium">
            + Tambah
          </Button>
        </div>
        {sortedExpenses.length === 0 ? (
          <div className="py-14 text-center">
            <Receipt className="mx-auto mb-2 h-8 w-8 text-zinc-700" />
            <p className="text-sm text-zinc-500">Belum ada pengeluaran</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Judul</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Category</th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Jumlah</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Catatan</th>
                </tr>
              </thead>
              <tbody>
                {sortedExpenses.map((exp, idx) => (
                  <tr
                    key={exp.id}
                    className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.02] ${idx % 2 === 0 ? 'bg-white/[0.01]' : ''}`}
                  >
                    <td className="px-5 py-3 text-zinc-400">{formatDate(exp.expense_date)}</td>
                    <td className="px-5 py-3 font-medium text-white">{exp.title}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center rounded-md bg-white/5 px-2 py-0.5 text-xs font-medium text-zinc-300 capitalize">
                        {exp.category}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-red-400">{formatCurrency(exp.amount)}</td>
                    <td className="px-5 py-3 text-zinc-500 max-w-[200px] truncate">{exp.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DepreciationContent() {
  return (
    <div className="rounded-sm border border-white/10 bg-zinc-950/40 py-16 text-center">
      <TrendingDown className="mx-auto mb-3 h-10 w-10 text-zinc-600" />
      <p className="text-sm text-zinc-400">Menu Depreciation akan segera tersedia.</p>
    </div>
  );
}
